/**
 * PmsShell — thin wrapper reused by every PMS Demo V1 admin surface.
 *
 *   AdminShell
 *     └── PmsDemoBanner  ("Vista previa · Datos de demostración")
 *     └── PmsSubNav      (Overview · Hoy · Calendario · …)
 *     └── {children}
 *
 * The demo banner is deliberately opinionated (locked copy from
 * `DEMO_BANNER`) so every screen carries the same warning without each page
 * re-declaring it.
 *
 * La barra de pestañas es la navegación DEL MÓDULO, y se ve en todos los
 * tamaños. Antes se ocultaba en escritorio porque la barra lateral del admin
 * repetía los diez destinos del PMS uno a uno; ahora la lateral lleva solo los
 * de producto (Hoy · Reservas · Huéspedes · Operaciones · Villas), así que
 * esta tira dejó de ser un duplicado y pasó a ser la única forma de llegar a
 * Overview, Calendario, Allocation y Ajustes PMS.
 */
import React from "react";
import { NavLink } from "react-router-dom";
import AdminShell from "@/pages/admin/AdminShell";
import {
  House, CalendarBlank, ClipboardText, Scales, Info,
  Broom, Wrench, Bed, Gear, Sun, Users,
} from "@phosphor-icons/react";
import { DEMO_BANNER, DEMO_MONTH_LABEL } from "@/data/pmsDemoData";

const PMS_TABS = [
  { key: "overview",     to: "/pms",              label: "Overview",     icon: House },
  { key: "today",        to: "/pms/today",        label: "Hoy",          icon: Sun },
  { key: "calendar",     to: "/pms/calendar",     label: "Calendario",   icon: CalendarBlank },
  { key: "reservations", to: "/pms/reservations", label: "Reservas",     icon: ClipboardText },
  { key: "guests",       to: "/pms/guests",       label: "Huéspedes",    icon: Users },
  { key: "allocation",   to: "/pms/allocation",   label: "Allocation",   icon: Scales },
  { key: "housekeeping", to: "/pms/housekeeping", label: "Housekeeping", icon: Broom },
  { key: "issues",       to: "/pms/issues",       label: "Incidencias",  icon: Wrench },
  { key: "ownerStays",   to: "/pms/owner-stays",  label: "Owner stays",  icon: Bed },
  { key: "settings",     to: "/pms/settings",     label: "Ajustes",      icon: Gear },
];

export default function PmsShell({ children }) {
  return (
    <AdminShell>
      <PmsDemoBanner />
      <PmsSubNav />
      <div>{children}</div>
    </AdminShell>
  );
}

export function PmsDemoBanner() {
  return (
    <div
      className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 px-4 py-3 flex items-start gap-3"
      data-testid="pms-demo-banner"
    >
      <Info size={18} className="text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" weight="fill" />
      <div className="min-w-0">
        <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
          {DEMO_BANNER.title}
        </div>
        <div className="text-xs text-amber-800 dark:text-amber-200 mt-0.5">
          {DEMO_BANNER.subtitle} · Periodo simulado: <span className="font-medium">{DEMO_MONTH_LABEL}</span>.
        </div>
      </div>
    </div>
  );
}

export function PmsSubNav() {
  return (
    <div className="mb-5 flex flex-wrap gap-1.5" data-testid="pms-subnav">
      {PMS_TABS.map((tab) => (
        <NavLink
          key={tab.key}
          to={tab.to}
          end={tab.to === "/pms"}
          data-testid={`pms-subnav-${tab.key}`}
          className={({ isActive }) => `
            inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full transition-colors border
            ${isActive
              ? "bg-stone-900 text-white border-stone-900 dark:bg-stone-100 dark:text-stone-900 dark:border-stone-100"
              : "bg-white text-stone-700 border-stone-300 hover:border-stone-500 dark:bg-stone-950 dark:text-stone-300 dark:border-stone-700 dark:hover:border-stone-500"}
          `}
        >
          <tab.icon size={12} weight="bold" />
          {tab.label}
          {tab.soon && (
            <span className="ml-1 text-[9px] uppercase tracking-wider bg-stone-200 text-stone-600 dark:bg-stone-800 dark:text-stone-400 px-1.5 py-0.5 rounded-full">
              Próximamente
            </span>
          )}
        </NavLink>
      ))}
    </div>
  );
}
